import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { HelpCenterSearchResult } from '../models/helpCenterSearchResult';
import { searchHelpCenter } from '../services/helpCenterService';
import { getSearchWords } from '../functions/highlightWords';

const MaxSuggestions = 8;
const SuggestionDelay = 300;

type HelpSearchFieldProps = {
  initialValue?: string;
};

/**
 * Hjälpcentrets sökfält. Medan man skriver fälls titlarna på de första träffarna ut under fältet; en titel öppnar
 * artikeln direkt. Enter utan vald titel eller knappen visar alla träffar på resultatsidan.
 */
export const HelpSearchField = (props: HelpSearchFieldProps) => {
  const { initialValue = '' } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [inputValue, setInputValue] = useState<string>(initialValue);
  const [suggestions, setSuggestions] = useState<HelpCenterSearchResult[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  // Bara det senaste anropet får fylla listan, så att ett sent svar för en tidigare bokstav inte skriver över.
  const latestRequest = useRef(0);

  const hasWords = getSearchWords(inputValue).length > 0;

  useEffect(() => {
    if (!hasWords) {
      // Ett svar som redan är på väg för det man suddat ut ska inte fylla listan senare.
      latestRequest.current += 1;
      setSuggestions([]);
      return undefined;
    }
    const request = latestRequest.current + 1;
    latestRequest.current = request;
    const timer = setTimeout(async () => {
      try {
        const result = await searchHelpCenter(inputValue, { skipErrorToast: true });
        if (latestRequest.current === request) setSuggestions(result.data.slice(0, MaxSuggestions));
      } catch (e) {
        ConsoleHelper.log(e);
        if (latestRequest.current === request) setSuggestions([]);
      }
    }, SuggestionDelay);
    return () => clearTimeout(timer);
  }, [inputValue, hasWords]);

  const showAllResults = (query: string) => {
    if (getSearchWords(query).length === 0) return;
    navigate(`${RoutesEnum.HelpSearch}?q=${encodeURIComponent(query.trim())}`, { replace: location.pathname === RoutesEnum.HelpSearch });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
      <Autocomplete<HelpCenterSearchResult, false, false, true>
        freeSolo
        fullWidth
        size='small'
        options={suggestions}
        // Servern har redan sökt; listan ska inte filtreras en gång till på titeln.
        filterOptions={(options) => options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
        // Fältet har inget valt värde, bara text. Annars minns det en vald artikel, markerar den nästa gång och Enter
        // öppnar den istället för resultatsidan.
        value={null}
        // Listan fälls ut först när det finns träffar, och de förra står kvar medan nya hämtas, så att den inte blinkar.
        open={open && hasWords && suggestions.length > 0}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputValue={inputValue}
        onInputChange={(event, value, reason) => {
          // 'reset' är Autocompletes egen återställning av texten; fältet styrs här (töms när en artikel väljs).
          if (reason !== 'reset') setInputValue(value);
          // Tömt fält på resultatsidan (X eller suddat) nollställer sökningen, så att sidan inte visar resultatet för en
          // sökning som inte längre står i fältet.
          if (reason !== 'reset' && value.trim() === '' && location.pathname === RoutesEnum.HelpSearch && location.search) {
            navigate(RoutesEnum.HelpSearch, { replace: true });
          }
        }}
        onChange={(event, value) => {
          if (value && typeof value !== 'string') {
            setInputValue('');
            const path = `${RoutesEnum.HelpArticle}/${encodeURIComponent(value.slug)}`;
            navigate(path, { replace: location.pathname === path });
          } else if (typeof value === 'string') showAllResults(value);
        }}
        renderOption={(optionProps, option) => (
          <Box component='li' {...optionProps} key={option.slug}>
            {option.title}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('help:searchPlaceholder')}
            inputProps={{ ...params.inputProps, 'aria-label': t('help:searchPlaceholder') }}
          />
        )}
      />
      <Button variant='contained' size='small' disabled={!hasWords} onClick={() => showAllResults(inputValue)} sx={{ height: '40px' }}>
        {t('common:button.search')}
      </Button>
    </Box>
  );
};
